import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { describeAxiosError } from "@/lib/api";
import {
  Mail, Lock, Eye, EyeOff, ArrowRight, MailCheck, ArrowLeft,
  ShieldCheck, ReceiptText, Boxes, Users, CloudUpload, FileBarChart2,
  Landmark, UserPlus, Phone, AtSign, UserRound,
} from "lucide-react";

/**
 * AXIORA — Login / Signup / OTP-verify screen.
 *
 * Two-panel layout matching the brand mock:
 *   ▸ Left panel  — dark navy hero with AXIORA logo lockup, headline,
 *                    six feature tiles, and a bar-chart illustration at
 *                    the bottom. Hidden on <lg screens (mobile shows the
 *                    right panel only, with a compact logo header).
 *   ▸ Right panel — white welcome card with a dashboard illustration,
 *                    email/password inputs (icons + show-password),
 *                    a blue→emerald gradient "Sign In" button, "OR"
 *                    divider, outlined "Create New Account" toggle,
 *                    and a "Your data is secure with AXIORA" footnote.
 *
 * Signup and OTP-verify flows are preserved from the previous version;
 * they now render inside the same welcome card via ``mode`` state.
 */

const FEATURES = [
  { icon: ReceiptText,  title: "GST Ready" },
  { icon: Boxes,        title: "Inventory Management" },
  { icon: Users,        title: "Payroll Management" },
  { icon: CloudUpload,  title: "Bank Statements Upload" },
  { icon: FileBarChart2,title: "Financial Reports" },
  { icon: Landmark,     title: "Income Tax" },
];

/** Small helper — the growing bar-chart artwork at the bottom of the left
 *  panel. Pure SVG so it stays crisp at every zoom level and doesn't add a
 *  network round-trip. */
function GrowthChart() {
  const bars = [22, 30, 38, 34, 46, 54, 62, 70, 82, 96, 110];
  return (
    <svg
      viewBox="0 0 480 200"
      className="w-full h-40 lg:h-48 opacity-90"
      preserveAspectRatio="none"
      aria-hidden
    >
      <defs>
        <linearGradient id="axiora-bar" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#3b82f6" />
          <stop offset="1" stopColor="#1e3a8a" />
        </linearGradient>
        <linearGradient id="axiora-line" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#3b82f6" />
          <stop offset="1" stopColor="#10b981" />
        </linearGradient>
      </defs>
      {bars.map((h, i) => {
        const x = 20 + i * 42;
        return (
          <rect
            key={i}
            x={x}
            y={180 - h}
            width={26}
            height={h}
            rx={2}
            fill="url(#axiora-bar)"
            opacity={0.9}
          />
        );
      })}
      <polyline
        points={bars
          .map((h, i) => `${20 + i * 42 + 13},${180 - h - 4}`)
          .join(" ")}
        fill="none"
        stroke="url(#axiora-line)"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {bars.map((h, i) => (
        <circle
          key={`d-${i}`}
          cx={20 + i * 42 + 13}
          cy={180 - h - 4}
          r={3}
          fill="#10b981"
        />
      ))}
    </svg>
  );
}

/** AXIORA logo lockup — the geometric "A" mark + wordmark + tagline. */
function AxioraLogo({ variant = "light" }) {
  const wordColor = variant === "light" ? "text-white" : "text-slate-900";
  return (
    <div className="flex items-center gap-3">
      <svg viewBox="0 0 64 64" className="w-14 h-14 flex-shrink-0" aria-hidden>
        <defs>
          <linearGradient id="ax-logo" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="#3b82f6" />
            <stop offset="1" stopColor="#10b981" />
          </linearGradient>
        </defs>
        {/* Stylised A: two upstroke bars + horizontal cross + arrow tip */}
        <path
          d="M32 6 L10 58 L20 58 L26 45 L38 45 L44 58 L54 58 Z"
          fill="url(#ax-logo)"
          opacity="0.95"
        />
        <path d="M28 34 L36 34 L34 40 L30 40 Z" fill="#0b1120" />
        <path d="M46 12 L58 12 L58 24" stroke="#10b981" strokeWidth="3" fill="none" strokeLinecap="round" />
        <path d="M46 24 L58 12" stroke="#10b981" strokeWidth="3" fill="none" strokeLinecap="round" />
      </svg>
      <div className="leading-tight">
        <div className={`text-3xl font-bold tracking-wider ${wordColor}`}>
          <span className="text-blue-400">AXIO</span>
          <span>RA</span>
        </div>
        <div className={`text-[10px] uppercase tracking-[0.28em] mt-0.5 ${variant === "light" ? "text-emerald-300/90" : "text-emerald-700"}`}>
          Smart Accounting. Stronger Business.
        </div>
      </div>
    </div>
  );
}

/** The small dashboard illustration above the welcome card. */
function DashboardIllustration() {
  return (
    <div className="relative mx-auto w-56 h-32 mb-4">
      <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-50 to-emerald-50 border border-slate-200 shadow-sm" />
      <div className="absolute top-3 left-3 right-3 h-4 rounded bg-white/80 border border-slate-100 flex items-center gap-1 px-2">
        <span className="w-1.5 h-1.5 rounded-full bg-red-300" />
        <span className="w-1.5 h-1.5 rounded-full bg-amber-300" />
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
      </div>
      <svg viewBox="0 0 220 80" className="absolute bottom-3 left-3 right-3 h-16">
        <defs>
          <linearGradient id="dbi-donut" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="#3b82f6" />
            <stop offset="1" stopColor="#10b981" />
          </linearGradient>
        </defs>
        <circle cx="24" cy="40" r="18" stroke="url(#dbi-donut)" strokeWidth="6" fill="none" strokeDasharray="80 40" />
        <rect x="60" y="18" width="46" height="46" rx="4" fill="#eef2ff" />
        <rect x="66" y="46" width="6" height="14" fill="#3b82f6" />
        <rect x="76" y="34" width="6" height="26" fill="#3b82f6" />
        <rect x="86" y="26" width="6" height="34" fill="#10b981" />
        <rect x="96" y="40" width="6" height="20" fill="#3b82f6" />
        <rect x="114" y="18" width="90" height="46" rx="4" fill="#f1f5f9" />
        <rect x="120" y="26" width="60" height="4" rx="1" fill="#cbd5e1" />
        <rect x="120" y="36" width="72" height="4" rx="1" fill="#cbd5e1" />
        <rect x="120" y="46" width="50" height="4" rx="1" fill="#cbd5e1" />
      </svg>
      <div className="absolute -right-2 -top-2 w-7 h-7 rounded-full bg-emerald-500 grid place-items-center shadow-lg">
        <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="3">
          <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}

export default function Login() {
  const { user, login, registerStart, registerVerify } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  // ``mode`` = "signin" | "signup" | "verify"
  const [mode, setMode] = useState("signin");
  const [signupOtp, setSignupOtp] = useState("");

  if (user) return <Navigate to="/" replace />;

  const switchMode = (next) => {
    setMode(next);
    setSignupOtp("");
  };

  const onLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(describeAxiosError(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  const onSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await registerStart({ email, password, name });
      setMode("verify");
      toast.success("Verification code sent — check your inbox");
    } catch (err) {
      toast.error(describeAxiosError(err, "Could not send code"));
    } finally {
      setLoading(false);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    if (signupOtp.length !== 6) {
      toast.error("Enter the 6-digit code from your email");
      return;
    }
    setLoading(true);
    try {
      await registerVerify(email, signupOtp);
      toast.success("Email verified — welcome to AXIORA");
      navigate("/");
    } catch (err) {
      toast.error(describeAxiosError(err, "Verification failed"));
    } finally {
      setLoading(false);
    }
  };

  const onResendOtp = async () => {
    setLoading(true);
    try {
      await registerStart({ email, password, name });
      setSignupOtp("");
      toast.success("New code sent");
    } catch (err) {
      toast.error(describeAxiosError(err, "Could not resend code"));
    } finally {
      setLoading(false);
    }
  };

  const gradientBtn =
    "w-full h-11 rounded-lg text-white font-semibold shadow-md " +
    "bg-gradient-to-r from-blue-700 via-blue-600 to-emerald-500 " +
    "hover:from-blue-800 hover:via-blue-700 hover:to-emerald-600 " +
    "disabled:opacity-60 disabled:cursor-not-allowed " +
    "flex items-center justify-center gap-2";

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-slate-50">
      {/* ── LEFT PANEL — dark hero ─────────────────────────────────────── */}
      <div className="relative overflow-hidden bg-slate-950 text-white hidden lg:flex flex-col justify-between p-10 xl:p-14">
        {/* Subtle grid backdrop */}
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#3b82f6 1px, transparent 1px), linear-gradient(90deg, #3b82f6 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="relative z-10">
          <AxioraLogo variant="light" />
        </div>

        <div className="relative z-10 mt-8 max-w-lg">
          <h1 className="text-4xl xl:text-5xl font-bold tracking-tight leading-[1.1]">
            Accounting
            <br />
            <span className="text-emerald-400">Reimagined.</span>
            <br />
            <span className="text-white/95">Powered by Intelligence.</span>
          </h1>
          <p className="mt-5 text-sm text-slate-300 leading-relaxed max-w-md">
            Axiora is an all-in-one accounting software that simplifies your business
            operations and gives you real-time clarity to grow with confidence.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-3 max-w-lg">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="rounded-lg border border-blue-400/25 bg-blue-950/40 backdrop-blur-sm p-3 flex flex-col items-center text-center hover:border-emerald-400/60 transition-colors"
                data-testid={`feature-tile-${f.title.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <f.icon className="w-6 h-6 text-emerald-400 mb-1.5" strokeWidth={1.6} />
                <div className="text-xs font-medium text-slate-200 leading-tight">{f.title}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 -mx-2 mt-8 pointer-events-none">
          <GrowthChart />
        </div>
      </div>

      {/* ── RIGHT PANEL — welcome card ─────────────────────────────────── */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile-only logo */}
          <div className="lg:hidden mb-6 flex justify-center">
            <AxioraLogo variant="dark" />
          </div>

          <DashboardIllustration />

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 sm:p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="welcome-heading">
                {mode === "signup" ? "Create your account" :
                 mode === "verify" ? "Verify your email" : "Welcome Back"}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {mode === "signup" ? "Get started with your AXIORA account" :
                 mode === "verify" ? `We sent a 6-digit code to ${email}` :
                 "Sign in to access your Axiora account"}
              </p>
            </div>

            {mode === "signin" && (
              <form onSubmit={onLogin} className="space-y-4" data-testid="form-login">
                <div>
                  <label htmlFor="email" className="text-sm font-medium text-slate-700 block mb-1.5">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email" type="email" autoComplete="email"
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="pl-10 h-11 rounded-lg border-slate-200 focus-visible:ring-blue-500"
                      data-testid="input-login-email" required
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="password" className="text-sm font-medium text-slate-700 block mb-1.5">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="password" type={showPassword ? "text" : "password"} autoComplete="current-password"
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="pl-10 pr-10 h-11 rounded-lg border-slate-200 focus-visible:ring-blue-500"
                      data-testid="input-login-password" required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      data-testid="btn-toggle-password"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <label className="inline-flex items-center gap-2 text-slate-600 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      data-testid="checkbox-remember-me"
                    />
                    Remember me
                  </label>
                  <Link to="/forgot-password" className="text-blue-600 hover:underline font-medium" data-testid="link-forgot-password">
                    Forgot Password?
                  </Link>
                </div>

                <Button type="submit" disabled={loading} className={gradientBtn} data-testid="btn-login-submit">
                  {loading ? "Signing in…" : (<>Sign In <ArrowRight className="w-4 h-4" /></>)}
                </Button>

                <div className="flex items-center gap-3 py-1">
                  <span className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-400 font-medium tracking-wider">OR</span>
                  <span className="flex-1 h-px bg-slate-200" />
                </div>

                <button
                  type="button"
                  onClick={() => switchMode("signup")}
                  className="w-full h-11 rounded-lg border border-blue-600 text-blue-700 font-semibold hover:bg-blue-50 flex items-center justify-center gap-2"
                  data-testid="btn-goto-signup"
                >
                  <UserPlus className="w-4 h-4" /> Create New Account
                </button>
              </form>
            )}

            {mode === "signup" && (
              <form onSubmit={onSendOtp} className="space-y-4" data-testid="form-register-step1">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1.5">Full name</label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
                         className="h-11 rounded-lg border-slate-200" data-testid="input-register-name" required />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1.5">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                           placeholder="you@example.com"
                           className="pl-10 h-11 rounded-lg border-slate-200"
                           data-testid="input-register-email" required />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1.5">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                           placeholder="At least 8 characters"
                           className="pl-10 h-11 rounded-lg border-slate-200"
                           data-testid="input-register-password" required minLength={8} />
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">Must include a letter and a digit.</p>
                </div>
                <Button type="submit" disabled={loading} className={gradientBtn} data-testid="btn-register-send-otp">
                  {loading ? "Sending code…" : (<>Send verification code <ArrowRight className="w-4 h-4" /></>)}
                </Button>
                <button
                  type="button"
                  onClick={() => switchMode("signin")}
                  className="w-full text-sm text-slate-500 hover:text-slate-900 flex items-center justify-center gap-1"
                  data-testid="btn-back-to-signin"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
                </button>
              </form>
            )}

            {mode === "verify" && (
              <form onSubmit={onVerify} className="space-y-4" data-testid="form-register-step2">
                <div className="flex items-start gap-2 text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                  <MailCheck className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-emerald-900">
                    Check your inbox for the 6-digit code.
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1.5">6-digit verification code</label>
                  <Input
                    type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                    autoComplete="one-time-code"
                    value={signupOtp}
                    onChange={(e) => setSignupOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    className="h-14 rounded-lg border-slate-200 text-center text-2xl tracking-[0.5em] font-mono"
                    data-testid="input-register-otp" required autoFocus
                  />
                </div>
                <Button type="submit" disabled={loading || signupOtp.length !== 6} className={gradientBtn} data-testid="btn-register-verify">
                  {loading ? "Verifying…" : (<>Verify &amp; create account <ArrowRight className="w-4 h-4" /></>)}
                </Button>
                <div className="flex items-center justify-between text-xs pt-1">
                  <button type="button"
                          onClick={() => switchMode("signup")}
                          className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                          data-testid="btn-register-back">
                    <ArrowLeft className="w-3 h-3" /> Change email
                  </button>
                  <button type="button" onClick={onResendOtp} disabled={loading}
                          className="text-blue-600 hover:underline disabled:opacity-50"
                          data-testid="btn-register-resend-otp">
                    Resend code
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500" data-testid="secure-footer">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            Your data is secure with Axiora
          </div>
        </div>
      </div>

      {/* ── Author / Support credit — bottom-right of the front page ───── */}
      <div
        className="fixed bottom-4 right-4 z-30 max-w-xs rounded-xl border border-slate-200 bg-white/95 backdrop-blur px-4 py-3 shadow-lg text-[11.5px] leading-relaxed text-slate-700"
        data-testid="author-credit-card"
      >
        <div className="flex items-center gap-1.5 font-semibold text-slate-900">
          <UserRound className="w-3.5 h-3.5 text-blue-700" />
          Created by ANKIT MARFATIA
        </div>
        <a
          href="tel:+919712128948"
          className="mt-1 flex items-center gap-1.5 text-slate-600 hover:text-blue-700"
          data-testid="author-phone"
        >
          <Phone className="w-3 h-3" /> Contact no : 9712128948
        </a>
        <a
          href="mailto:ankmarfatia@yahoo.co.in"
          className="flex items-center gap-1.5 text-slate-600 hover:text-blue-700 truncate"
          data-testid="author-email"
        >
          <AtSign className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">Email id : ankmarfatia@yahoo.co.in</span>
        </a>
        <div className="mt-1.5 pt-1.5 border-t border-slate-100 text-[10.5px] text-slate-500 italic">
          Please contact me for any kind of support or query.
        </div>
      </div>
    </div>
  );
}
